import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { compact, day, qty } from '../lib/format.js';
import { ROLES, can } from '../lib/roles.js';
import { Alert, Card, CardSkeleton, Empty, LoadMore, Pill, useAsync, usePagedList } from '../components/ui.jsx';
import PeriodPicker, { useUrlPeriod } from '../components/PeriodPicker.jsx';
import ExportButton, { fetchAllPages, periodSlug, periodText } from '../components/ExportButton.jsx';
import { useEntityQuery } from '../lib/entity.js';

// Open orders and drafts are work still to do, so they list every date unless a period is picked.
const VIEWS = [
  { key: 'issued,partially_delivered', label: 'Open', pending: true },
  { key: 'draft', label: 'Drafts', pending: true },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: '', label: 'All' },
];

export default function DeliveryOrders() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? 'issued,partially_delivered';
  const pending = VIEWS.some((v) => v.pending && v.key === status);
  const entityQuery = useEntityQuery();
  const { period, setPeriod, query } = useUrlPeriod({ allByDefault: pending });
  const list = usePagedList(`/api/delivery-orders?${[status && `status=${status}`, query, entityQuery].filter(Boolean).join('&')}`, 'delivery_orders');
  const [open, setOpen] = useState(null);
  const session = useSession();
  const canRaise = can(session, ROLES.COUNTER);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Delivery orders</h1>
          <div className="page-sub">
            Document 1 of the sale. The counter states the goods, the buyer and the godown; the godown works from this.
          </div>
        </div>
        <div className="inline">
          <div className="segmented" role="group" aria-label="View">
            {VIEWS.map((v) => (
              <button key={v.label} aria-pressed={status === v.key}
                onClick={() => setParams(v.key ? { status: v.key } : {})}>{v.label}</button>
            ))}
          </div>
          <PeriodPicker allowAll value={period} onChange={setPeriod} />
          <ExportButton build={async () => ({
            filename: `delivery-orders-${periodSlug(period)}`,
            title: 'Delivery orders',
            subtitle: `${(VIEWS.find((v) => v.key === status) || { label: 'All' }).label} · ${periodText(period)}`,
            sheets: [{
              name: 'Delivery orders',
              columns: [
                { key: 'do_number', header: 'Order' }, { key: 'do_date', header: 'Date', type: 'date' },
                { key: 'buyer_name', header: 'Buyer' }, { key: 'godown_name', header: 'Godown' },
                { key: 'line_count', header: 'Lines', type: 'int' },
                { key: 'qty_pending', header: 'Pending qty', type: 'number', total: true },
                { key: 'status', header: 'Status' },
              ],
              rows: await fetchAllPages(
                `/api/delivery-orders?${[status && `status=${status}`, query, entityQuery].filter(Boolean).join('&')}`,
                'delivery_orders'),
            }],
          })} />
          {canRaise && <Link className="btn" to="/delivery-orders/new"><span aria-hidden="true">＋</span> New</Link>}
        </div>
      </div>

      <Alert onRetry={list.reload}>{list.error}</Alert>
      {list.loading && !list.data && <CardSkeleton lines={5} />}

      {list.data && (
        <Card pad={list.data.delivery_orders.length === 0}>
          {list.data.delivery_orders.length === 0 ? (
            <Empty title="Nothing here"
              action={canRaise ? <Link className="btn" to="/delivery-orders/new">Raise a delivery order</Link> : null}>
              No delivery order matches this view.
            </Empty>
          ) : (
            <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
              <table>
                <thead>
                  <tr><th>Order</th><th>Buyer</th><th>Godown</th><th className="r">Lines</th>
                    <th className="r">Pending</th><th>Status</th><th /></tr>
                </thead>
                <tbody>
                  {list.data.delivery_orders.map((o) => (
                    <tr key={o.id} className="clickable" onClick={() => setOpen(open === o.id ? null : o.id)}>
                      <td className="mono">{o.do_number || 'draft'}<div className="sub">{day(o.do_date)}</div></td>
                      <td>{o.buyer_name}<div className="sub">Place of supply {o.place_of_supply}</div></td>
                      <td>{o.godown_name}</td>
                      <td className="r num">{o.line_count}</td>
                      <td className="r num">{compact(o.qty_pending)}</td>
                      <td><Pill value={o.status} /></td>
                      <td className="r muted" aria-hidden="true">{open === o.id ? '▴' : '▾'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <LoadMore list={list} />
        </Card>
      )}

      {open && <OrderDetail id={open} onChanged={list.reload} />}
    </>
  );
}

function OrderDetail({ id, onChanged }) {
  const detail = useAsync(() => api.get(`/api/delivery-orders/${id}`), [id]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const session = useSession();
  const counter = can(session, ROLES.COUNTER);
  const godown = can(session, ROLES.GODOWN);
  if (detail.loading && !detail.data) return <CardSkeleton lines={3} />;
  if (detail.error) return <Alert onRetry={detail.reload}>{detail.error}</Alert>;
  if (!detail.data) return null;
  const o = detail.data;

  const act = (path, question) => async () => {
    if (question && !window.confirm(question)) return;
    try { await api.post(path); detail.reload(); onChanged(); }
    catch (e) { setError(e.message); }
  };

  return (
    <Card title={`${o.do_number || 'Draft'} — ${o.buyer_name}`} note={`${o.godown_name} · ${day(o.do_date)}`}
      actions={(
        <>
          {counter && o.status === 'draft' && <button className="btn small" onClick={act(`/api/delivery-orders/${id}/issue`)}>Issue order</button>}
          {godown && ['issued', 'partially_delivered'].includes(o.status) &&
            <button className="btn small" onClick={() => navigate('/godown')}>Despatch from godown</button>}
          {counter && o.status !== 'cancelled' && o.status !== 'delivered' &&
            <button className="btn small secondary"
              onClick={act(`/api/delivery-orders/${id}/cancel`, `Cancel ${o.do_number || 'this draft order'}? This cannot be undone.`)}>
              Cancel
            </button>}
        </>
      )}>
      <Alert>{error}</Alert>
      <div className="row" style={{ marginBottom: 16 }}>
        <div><div className="label">Status</div><Pill value={o.status} /></div>
        <div><div className="label">Buyer GSTIN</div><span className="mono">{o.buyer_gstin || 'Unregistered'}</span></div>
        <div><div className="label">Place of supply</div>{o.place_of_supply}</div>
        <div><div className="label">Vehicle</div>{o.vehicle_no || '—'}</div>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>#</th><th>Item</th><th className="r">Ordered</th><th className="r">Despatched</th>
            <th className="r">Pending</th><th className="r">Rate</th></tr></thead>
          <tbody>
            {o.lines.map((l) => (
              <tr key={l.id}>
                <td className="num">{l.line_no}</td>
                <td>{l.item_name}<div className="sub">HSN {l.hsn || '—'} · GST {l.gst_rate}%</div></td>
                <td className="r num">{qty(l.qty, l.uom)}</td>
                <td className="r num">{qty(l.qty_delivered)}</td>
                <td className="r num"><b>{qty(l.qty_pending)}</b></td>
                <td className="r num">{l.rate ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
